import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import i18n from "@/i18n";
import { agentsApi, billingApi, inviteApi, invitesApi, orgUsersApi, orgsApi, reviewApi, revenueApi, seoApi, shieldApi, usageApi, websiteApi, type BillingAction } from "@/lib/api";
import type { UsageOverview } from "@/lib/usage-contract";
import type { AgentsOverview } from "@/lib/agents-contract";
import { mergeResearchKeywords } from "@/lib/seo-keywords";
import type {
  AccessRequestRow,
  ApplicationRow,
  BillingOverview,
  CustomerRow,
  DownloadNotifyRow,
  GatedLeadRow,
  GlobalUserRow,
  HelpFeedbackRow,
  ImportKeywordSource,
  KeywordEditorialPatch,
  KeywordFacets,
  KeywordImportResult,
  KeywordPage,
  KeywordQuery,
  LeadRow,
  MemberRow,
  NewsletterRow,
  OrgUserRow,
  PendingInviteRow,
  AiAppLite,
  PolicyWithTargets,
  RevenueSummary,
  ReviewMintResult,
  ReviewStatusData,
  ReviewTemplate,
  ReviewVariantsData,
  SeoData,
  SeoKeyword,
  SeoKeywordResearchData,
  SeoKeywordResearchRow,
  SignupCodeRow,
} from "@/types/admin";

/** Surface a server-side truncation (list caps at 20 pages × 1000 rows, #186)
 *  as a warning toast — deduped per resource via the sonner id. */
function warnTruncated<T extends { truncated?: boolean }>(resource: string) {
  return (r: T): T => {
    if (r.truncated) toast.warning(i18n.t("table.truncatedWarning"), { id: `truncated-${resource}` });
    return r;
  };
}

type WebsiteResource = "newsletter" | "applications" | "leads";
/** Resources the console may hard-delete (GDPR erasure) — the PII tables. */
type DeletableResource = WebsiteResource | "gated_leads" | "download_notify" | "help_feedback";

export function useNewsletter() {
  return useQuery({
    queryKey: ["website", "newsletter"],
    queryFn: () =>
      websiteApi<{ rows: NewsletterRow[]; truncated?: boolean }>({ op: "list", resource: "newsletter" })
        .then(warnTruncated("newsletter"))
        .then((r) => r.rows),
  });
}

export function useApplications() {
  return useQuery({
    queryKey: ["website", "applications"],
    queryFn: () =>
      websiteApi<{ rows: ApplicationRow[]; truncated?: boolean }>({ op: "list", resource: "applications" })
        .then(warnTruncated("applications"))
        .then((r) => r.rows),
  });
}

export function useLeads() {
  return useQuery({
    queryKey: ["website", "leads"],
    queryFn: () =>
      websiteApi<{ rows: LeadRow[]; truncated?: boolean }>({ op: "list", resource: "leads" })
        .then(warnTruncated("leads"))
        .then((r) => r.rows),
  });
}

export function useCustomers() {
  return useQuery({
    queryKey: ["shield", "customers"],
    queryFn: () =>
      shieldApi<{ rows: CustomerRow[]; truncated?: boolean }>({ op: "list" })
        .then(warnTruncated("customers"))
        .then((r) => r.rows),
  });
}

/** Update an allow-listed field (status / notes) on a website resource row. */
export function useUpdateRow(resource: WebsiteResource) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; patch: Record<string, unknown> }) =>
      websiteApi<{ row: unknown }>({ op: "update", resource, id: vars.id, patch: vars.patch }).then((r) => r.row),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["website", resource] }),
  });
}

/** Hard-delete a row from a website PII resource (GDPR erasure; irreversible). */
export function useDeleteRow(resource: DeletableResource) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => websiteApi<{ ok: boolean }>({ op: "delete", resource, id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["website", resource] }),
  });
}

/** Bulk hard-delete rows from a website PII resource (batch actions; irreversible). */
export function useBulkDeleteRow(resource: DeletableResource) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => websiteApi<{ ok: boolean; deleted: number }>({ op: "delete", resource, ids }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["website", resource] }),
  });
}

/** Bulk-apply an allow-listed patch (e.g. status) to many rows in one round-trip. */
export function useBulkUpdateRow(resource: WebsiteResource) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { ids: string[]; patch: Record<string, unknown> }) =>
      websiteApi<{ ok: boolean; updated: number }>({ op: "update", resource, ids: vars.ids, patch: vars.patch }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["website", resource] }),
  });
}

/** Mint a short-lived signed URL for a CV in the private resumes bucket. */
export async function fetchSignedCvUrl(path: string): Promise<string> {
  const { url } = await websiteApi<{ url: string }>({ op: "signed_url", path });
  return url;
}

/** /download notify-me signups (read-only; the website notify endpoint owns writes). */
export function useDownloadNotify() {
  return useQuery({
    queryKey: ["website", "download_notify"],
    queryFn: () =>
      websiteApi<{ rows: DownloadNotifyRow[]; truncated?: boolean }>({ op: "list", resource: "download_notify" })
        .then(warnTruncated("download_notify"))
        .then((r) => r.rows),
  });
}

/** Help-center feedback votes (read-only; the website help pages own writes). */
export function useHelpFeedback() {
  return useQuery({
    queryKey: ["website", "help_feedback"],
    queryFn: () => websiteApi<{ rows: HelpFeedbackRow[] }>({ op: "list", resource: "help_feedback" }).then((r) => r.rows),
  });
}

/** Gated-content leads (read-only; the public report pipeline owns writes). */
export function useGatedLeads() {
  return useQuery({
    queryKey: ["website", "gated_leads"],
    queryFn: () =>
      websiteApi<{ rows: GatedLeadRow[]; truncated?: boolean }>({ op: "list", resource: "gated_leads" })
        .then(warnTruncated("gated_leads"))
        .then((r) => r.rows),
  });
}

/** Read an allow-listed site_settings value (e.g. drip_auto_start). */
export function useSiteSetting<T = unknown>(key: string) {
  return useQuery({
    queryKey: ["website", "site_settings", key],
    queryFn: () => websiteApi<{ value: T | null }>({ op: "get_setting", key }).then((r) => r.value),
  });
}

/** Write an allow-listed site_settings value. */
export function useSetSiteSetting(key: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (value: unknown) => websiteApi<{ value: unknown }>({ op: "set_setting", key, value }).then((r) => r.value),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["website", "site_settings", key] }),
  });
}

/** Mint a short-lived signed URL for an enrichment asset in `gated-assets`. */
export async function fetchSignedGatedUrl(path: string): Promise<string> {
  const { url } = await websiteApi<{ url: string }>({ op: "signed_url", path, bucket: "gated-assets" });
  return url;
}

/**
 * Drip control for a gated lead (start/stop/resend). Proxied server-side to the
 * website's /api/drip (qadar.ai#216). `start` throws "no_consent" (409) when the
 * lead has no marketing consent.
 */
export function useDripAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; action: "start" | "stop" | "resend" }) =>
      websiteApi<{ ok?: boolean; status?: string }>({ op: "drip", id: vars.id, action: vars.action }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["website", "gated_leads"] }),
  });
}

/**
 * Promote a gated lead into the sales `leads` pipeline (dedupe by email).
 * Returns `{ promoted: true }` on insert or `{ already: true }` when the email is
 * already a lead. Refreshes the gated list (`already_lead`) and the leads list.
 */
export function usePromoteLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => websiteApi<{ promoted?: boolean; already?: boolean; lead_id?: string }>({ op: "promote", id }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["website", "gated_leads"] });
      qc.invalidateQueries({ queryKey: ["website", "leads"] });
    },
  });
}

export function useSeo(from: string, to: string) {
  return useQuery({
    queryKey: ["seo", from, to],
    queryFn: () => seoApi<SeoData>({ op: "data", from, to }),
  });
}

/**
 * Set or clear the deliberately chosen focus keyword and editable meta tags for an
 * SEO target row. Routed through the service-role `admin-website` function: the
 * client may only supply `focus_keyword` (empty clears it) and the three meta
 * fields (`meta_title`, `meta_description`, `og_image`). The server stamps
 * focus_source='human_override' / focus_updated_at / focus_updated_by and, when
 * any meta field changes, `meta_updated_at` — those are never trusted from the
 * client. Reads still flow through `admin-seo` (which joins the ranking history),
 * so we invalidate the `seo` query on success.
 */
export function useSetSeoFocus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      id: string;
      focus_keyword?: string;
      meta_title?: string | null;
      meta_description?: string | null;
      og_image?: string | null;
    }) => {
      const { id, ...patch } = vars;
      return websiteApi<{ row: SeoKeyword }>({
        op: "update",
        resource: "seo_keywords",
        id,
        patch,
      }).then((r) => r.row);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["seo"] }),
  });
}

/**
 * Promote the SEO agent's proposed focus keyword into focus_keyword. The server
 * copies it from the stored row (focus_source='accepted') — the keyword is never
 * sent by the client, so the proposal cannot be forged.
 */
export function useAcceptSeoProposal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => websiteApi<{ row: SeoKeyword }>({ op: "accept_keyword", id }).then((r) => r.row),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["seo"] }),
  });
}

/**
 * Keyword-research corpus (qadar.ai#333/#334), server-side paged/filtered/sorted
 * (qadar-admin#132). The corpus is 41K+ rows, so the browser only ever holds one
 * page: paging/filters/search/sort are query inputs, the edge-op returns that
 * page's raw rows + the canonical aggregate for those keywords + an exact filtered
 * `total`. We merge per page (mergeResearchKeywords) and surface `total` to the
 * pagination footer. keepPreviousData avoids a flash while paging/filtering.
 */
export function useKeywordResearch(query: KeywordQuery) {
  return useQuery({
    queryKey: ["seo", "keyword-research", query],
    queryFn: () =>
      seoApi<SeoKeywordResearchData>({
        op: "keyword_research",
        page: query.page,
        pageSize: query.pageSize,
        search: query.search || undefined,
        is_question: query.isQuestion || undefined,
        intent: query.intents,
        answer_status: query.answerStatuses,
        seed: query.seeds,
        locales: query.locales,
        scope: query.scope,
        sort: query.sort,
      }).then((d) => ({ rows: mergeResearchKeywords(d.rows, d.canonical), total: d.total }) satisfies KeywordPage),
    placeholderData: keepPreviousData,
  });
}

/**
 * Distinct filter facets (locales / intents / topics) for the Keywords + Questions
 * dropdowns. The client no longer holds the full corpus (#132), so these come from
 * admin-seo, scoped by is_question. Cached hard — facets change rarely.
 */
export function useKeywordFacets(isQuestion?: boolean) {
  return useQuery({
    queryKey: ["seo", "keyword-facets", Boolean(isQuestion)],
    queryFn: () => seoApi<KeywordFacets>({ op: "keyword_facets", is_question: isQuestion || undefined }),
    staleTime: 5 * 60_000,
  });
}

/**
 * Editorial update of a single research keyword: URL assignment and the
 * People-Also-Ask answering queue. Only the four editorial fields are writable;
 * the server validates the status/mode vocabulary and stamps updated_at.
 */
export function useUpdateKeyword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; patch: KeywordEditorialPatch }) =>
      seoApi<{ row: SeoKeywordResearchRow }>({ op: "keyword_update", id: vars.id, patch: vars.patch }).then((r) => r.row),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["seo", "keyword-research"] }),
  });
}

/**
 * Hard-delete research keyword(s) by id — the per-row trash button (and batch
 * cleanup). Runs only through the admin-authed service-role function; the row
 * RLS bypass lives server-side. Refetches the corpus on success.
 */
export function useDeleteKeyword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { ids: string[] }) =>
      seoApi<{ deleted: number }>({ op: "keyword_delete", ids: vars.ids }).then((r) => r.deleted),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["seo", "keyword-research"] });
      qc.invalidateQueries({ queryKey: ["seo", "keyword-facets"] });
    },
  });
}

/**
 * Upload a keyword CSV (ATP / Sistrix). The browser sends the raw CSV + source/
 * locale to admin-seo, which forwards it to qadar.ai's canonical import endpoint
 * with the shared secret (never in the browser). On success the research corpus
 * is refetched so the new rows appear.
 */
export function useImportKeywords() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { csv: string; source?: ImportKeywordSource; language?: string; country?: string }) =>
      seoApi<KeywordImportResult>({ op: "import_keywords", ...vars }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["seo", "keyword-research"] });
      qc.invalidateQueries({ queryKey: ["seo", "keyword-facets"] });
    },
  });
}

export function useRevenue(range?: { from: number; to: number }) {
  return useQuery({
    queryKey: ["revenue", range?.from ?? null, range?.to ?? null],
    queryFn: () => revenueApi<RevenueSummary>({ op: "summary", ...(range ?? {}) }),
  });
}

export function useUsageOverview() {
  return useQuery({
    queryKey: ["usage", "overview"],
    queryFn: () => usageApi<UsageOverview>(),
  });
}

export function useAgentsOverview() {
  return useQuery({
    queryKey: ["agents", "overview"],
    queryFn: () => agentsApi<AgentsOverview>("/overview"),
    refetchInterval: 30_000,
  });
}

export function useKillAgents() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reason: string) => agentsApi<{ epoch: number; active: true }>("/kill", { reason }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["agents"] }),
  });
}

export function useSetAgentPaused() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { agentId: string; paused: boolean; reason?: string }) =>
      agentsApi<{ agent_id: string; state: string }>(vars.paused ? "/pause" : "/resume", { agent_id: vars.agentId, ...(vars.paused && vars.reason ? { reason: vars.reason } : {}) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["agents"] }),
  });
}

export function useMembers() {
  return useQuery({
    queryKey: ["members"],
    queryFn: () => inviteApi<{ rows: MemberRow[] }>({ op: "list" }).then((r) => r.rows),
  });
}

export function useInviteMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (email: string) => inviteApi<{ row: unknown }>({ op: "invite", email }).then((r) => r.row),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["members"] }),
  });
}

export function useDeleteMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => inviteApi<{ ok: boolean }>({ op: "delete", id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["members"] }),
  });
}

export function useOrgMembers(orgId: string | undefined) {
  return useQuery({
    queryKey: ["shield", "org-members", orgId],
    queryFn: () => {
      if (!orgId) throw new Error("orgId required");
      return orgsApi<{ rows: OrgUserRow[] }>({ op: "members", id: orgId }).then((r) => r.rows);
    },
    enabled: Boolean(orgId),
  });
}

export function useAllUsers() {
  return useQuery({
    queryKey: ["shield", "users"],
    queryFn: () => orgUsersApi<{ rows: GlobalUserRow[] }>({ op: "list" }).then((r) => r.rows),
  });
}

export function useUpdateOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; patch: Record<string, unknown> }) =>
      orgsApi<{ row: unknown }>({ op: "update", ...vars }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shield"] }),
  });
}

export function useOrgLifecycle(op: "soft_delete" | "restore" | "hard_purge") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; confirm_name?: string; override_grace?: boolean }) => orgsApi<{ ok: boolean }>({ op, ...vars }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shield"] }),
  });
}

/**
 * Admin-initiated new-customer onboarding (#51, v1). Service-role invite on the
 * Shield `admin-users` function → `inviteUserByEmail`; the `handle_new_user`
 * trigger provisions org + admin role on first sign-in. The invitee only appears
 * in the users list once they accept, so the refetch is best-effort.
 */
export function useInviteOrgUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { email: string; org_name: string; industry?: string; plan?: string; locale?: "de" | "en" }) =>
      orgUsersApi<{ ok: boolean; user_id: string | null; email_sent?: boolean }>({ op: "invite", ...vars }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shield"] }),
  });
}

export function useUpdateOrgUser(orgId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; patch: { full_name?: string; role?: string } }) =>
      orgUsersApi<{ ok: boolean }>({ op: "update", org_id: orgId, ...vars }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shield"] }),
  });
}

export function useDeleteOrgUser(orgId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string }) => orgUsersApi<{ ok: boolean }>({ op: "delete", org_id: orgId, ...vars }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shield"] }),
  });
}

// ── Policies (#97) ──────────────────────────────────────────────────────────
// View + edit the SELECTED org's policies cross-org via admin-orgs (service-role
// behind the staff gate). Shape mirrors shield-control #309; shield-web reads the
// same policy_targets, so the targets shape is load-bearing — keep it stable.
type PolicyInput = {
  name: string;
  description?: string | null;
  enforcement: string;
  config: { data: string[]; actions: string[] };
};

export function useOrgPolicies(orgId: string | undefined) {
  return useQuery({
    queryKey: ["shield", "org-policies", orgId],
    queryFn: () => {
      if (!orgId) throw new Error("orgId required");
      return orgsApi<{ policies: PolicyWithTargets[]; apps: AiAppLite[] }>({ op: "policies_list", id: orgId });
    },
    enabled: Boolean(orgId),
  });
}

export function useCreatePolicy(orgId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { policy: PolicyInput; targets: { ai_app_id: string }[] }) =>
      orgsApi<{ ok: boolean; id: string }>({ op: "policy_create", id: orgId, ...vars }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shield", "org-policies", orgId] }),
  });
}

export function useUpdatePolicy(orgId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { policy_id: string; policy: PolicyInput; targets: { ai_app_id: string }[] }) =>
      orgsApi<{ ok: boolean }>({ op: "policy_update", id: orgId, ...vars }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shield", "org-policies", orgId] }),
  });
}

export function useTogglePolicy(orgId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { policy_id: string; enabled: boolean }) =>
      orgsApi<{ ok: boolean }>({ op: "policy_toggle", id: orgId, ...vars }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shield", "org-policies", orgId] }),
  });
}

export function useDeletePolicy(orgId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { policy_id: string }) => orgsApi<{ ok: boolean }>({ op: "policy_delete", id: orgId, ...vars }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shield", "org-policies", orgId] }),
  });
}

export function useBillingOverview(orgId: string | undefined) {
  return useQuery({
    queryKey: ["shield", "billing", orgId],
    queryFn: () => {
      if (!orgId) throw new Error("orgId required");
      return billingApi<BillingOverview>({ action: "get_overview", org_id: orgId });
    },
    enabled: Boolean(orgId),
  });
}

/**
 * Billing write-through: the action hits Stripe; the org-row mirror
 * (plan/subscription_status) follows via webhook within ~2s — hence the
 * immediate overview refetch plus a delayed broad invalidation.
 */
export function useBillingAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { action: Exclude<BillingAction, "get_overview">; org_id: string } & Record<string, unknown>) =>
      billingApi<{ ok: boolean }>(vars),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["shield", "billing", vars.org_id] });
      setTimeout(() => qc.invalidateQueries({ queryKey: ["shield"] }), 2500);
    },
  });
}

// ---- Invite-only onboarding management (#63) -------------------------------
// All read/write through the service-role `admin-invites` Shield function; every
// mutation is audited server-side to admin_audit. The actual invite EMAIL is not
// sent here (the staff-gated send-invite path is a separate shield-control
// ticket) — the UI stubs resend/send accordingly.

export function usePendingInvites() {
  return useQuery({
    queryKey: ["shield", "invites"],
    queryFn: () => invitesApi<{ rows: PendingInviteRow[] }>({ op: "invitations_list" }).then((r) => r.rows),
  });
}

export function useRevokeInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => invitesApi<{ ok: boolean }>({ op: "invitation_revoke", id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shield", "invites"] }),
  });
}

export function useCreateInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { org_id: string; email: string; role: string; locale?: "de" | "en" }) =>
      invitesApi<{ ok: boolean; id: string; email_sent: boolean; deduped?: boolean }>({ op: "invitation_create", ...vars }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shield", "invites"] }),
  });
}

/** Re-send the redemption-link email for an existing open invitation. */
export function useResendInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => invitesApi<{ ok: boolean; email_sent: boolean }>({ op: "invitation_resend", id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shield", "invites"] }),
  });
}

export function useAccessRequests() {
  return useQuery({
    queryKey: ["shield", "access-requests"],
    queryFn: () => invitesApi<{ rows: AccessRequestRow[] }>({ op: "access_requests_list" }).then((r) => r.rows),
  });
}

export function useUpdateAccessRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; status: string }) => invitesApi<{ ok: boolean }>({ op: "access_request_update", ...vars }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shield", "access-requests"] }),
  });
}

export function useSignupCodes() {
  return useQuery({
    queryKey: ["shield", "signup-codes"],
    queryFn: () => invitesApi<{ rows: SignupCodeRow[] }>({ op: "signup_codes_list" }).then((r) => r.rows),
  });
}

export function useCreateSignupCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { org_id: string; code: string; role: string; max_uses?: number | null; expires_at?: string | null }) =>
      invitesApi<{ ok: boolean; id: string }>({ op: "signup_code_create", ...vars }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shield", "signup-codes"] }),
  });
}

export function useRevokeSignupCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => invitesApi<{ ok: boolean }>({ op: "signup_code_revoke", id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shield", "signup-codes"] }),
  });
}

// ---- Review Hub (#73) — proxied/read via admin-review (website service-role) ---

export function useReviewStatus() {
  return useQuery({
    queryKey: ["review", "status"],
    queryFn: () => reviewApi<ReviewStatusData & { truncated?: boolean }>({ op: "status" }).then(warnTruncated("review_status")),
  });
}

export function useReviewVariants() {
  return useQuery({
    queryKey: ["review", "variants"],
    queryFn: () => reviewApi<ReviewVariantsData>({ op: "variants" }),
  });
}

/** Mint review invites (proxied to the website's secret-gated endpoint). */
export function useMintReviews() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { recipients: { name?: string; email?: string; phone?: string; locale?: string }[]; send?: boolean }) =>
      reviewApi<ReviewMintResult>({ op: "mint", recipients: vars.recipients, send: vars.send }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["review"] }),
  });
}

/** Fetch the Resend broadcast template (subject/html/text + merge field) on demand. */
export function useReviewTemplate() {
  return useMutation({
    mutationFn: (locale: "en" | "de") => reviewApi<ReviewTemplate>({ op: "template", locale }),
  });
}
